/**
 * Auth provider component - initializes auth state
 * Uses Supabase-style server-side session management
 */

import {useEffect, useState} from 'react';
import {useAuthStore} from '@voxapilot/shared';
import {isElectron} from '../../utils/electron';

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({children}: AuthProviderProps) {
  const {initialize, cleanup} = useAuthStore();
  const [apiLoaded, setApiLoaded] = useState(false);
  const [apiModule, setApiModule] = useState<{api: any} | null>(null);
  const [initialized, setInitialized] = useState(false); // Track if we've already initialized

  // Lazy load Firebase config to prevent Electron crashes
  useEffect(() => {
    const loadApi = async () => {
      try {
        const firebaseModule = await import('../../config/firebase');
        setApiModule(firebaseModule);
        setApiLoaded(true);
      } catch (error) {
        console.error('Failed to load Firebase config:', error);
        setApiLoaded(true); // Set to true even on error to proceed with Firebase Auth only
      }
    };
    
    loadApi();
  }, []);

  useEffect(() => {
    // Only initialize once
    if (initialized) return;
    if (!apiLoaded) return; // Wait for API to load (or fail to load)
    
    // Initialize auth state
    // In Electron, skip session API (cookies don't work well)
    // In web browsers, use session API (Supabase-style)
    const initAuth = async () => {
      try {
        // CRITICAL: Ensure Firebase app is initialized BEFORE calling initialize()
        // This ensures the frontend's Firebase config (with correct env vars) is used
        // The shared package's getFirebaseAuth() will use the existing app if available
        // CRITICAL: Force initialization of Firebase app from frontend config FIRST
        // This ensures the frontend's Firebase config (with correct env vars) is used
        // The shared package's getFirebaseAuth() will detect and reuse this app
        try {
          const firebaseModule = await import('../../config/firebase');
          const {getApps} = await import('firebase/app');
          
          // Check if Firebase app is already initialized
          let existingApps = getApps();
          
          if (existingApps.length === 0) {
            // No app exists, force initialization by accessing the app proxy
            // Accessing the proxy triggers getApp() which initializes with correct config
            console.log('[AuthProvider] 🔄 Initializing Firebase app from frontend config...');
            const app = firebaseModule.app;
            
            // Access properties to trigger the proxy getter, which calls getApp()
            // This initializes Firebase with the frontend's config (correct env vars)
            void app.name; // Trigger proxy getter
            void app.options; // Trigger proxy getter
            
            // Verify app was initialized
            existingApps = getApps();
            if (existingApps.length > 0) {
              const initializedApp = existingApps[0];
              console.log('[AuthProvider] ✅ Firebase app initialized from frontend config');
              console.log('[AuthProvider] App name:', initializedApp.name);
              console.log('[AuthProvider] API Key present:', !!initializedApp.options?.apiKey && initializedApp.options.apiKey !== '');
              console.log('[AuthProvider] API Key length:', initializedApp.options?.apiKey?.length || 0);
              
              // Verify API key is valid (not placeholder)
              if (!initializedApp.options?.apiKey || 
                  initializedApp.options.apiKey === '' || 
                  initializedApp.options.apiKey === 'MISSING_API_KEY' ||
                  initializedApp.options.apiKey === 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY') {
                console.error('[AuthProvider] ❌ Firebase app initialized but API key is invalid!');
                console.error('[AuthProvider] API Key value:', initializedApp.options?.apiKey);
                throw new Error('Firebase API key is invalid. Check .env.local and rebuild frontend.');
              }
            } else {
              console.error('[AuthProvider] ❌ Firebase app proxy accessed but app was not initialized!');
              throw new Error('Firebase app initialization failed');
            }
          } else {
            console.log('[AuthProvider] ✅ Firebase app already exists, reusing it');
            const existingApp = existingApps[0];
            console.log('[AuthProvider] Existing app name:', existingApp.name);
            console.log('[AuthProvider] Existing app API Key present:', !!existingApp.options?.apiKey && existingApp.options.apiKey !== '');
            console.log('[AuthProvider] Existing app API Key length:', existingApp.options?.apiKey?.length || 0);
          }
        } catch (firebaseError) {
          console.error('[AuthProvider] ❌ Failed to initialize Firebase app:', firebaseError);
          // Log error but continue - app should still work for public pages
          // Don't throw - allow app to continue without Firebase
        }
        
        // FIXED: Ensure Firebase app is fully initialized and validated before auth store uses it
        // Wait for app to be initialized with valid config
        const {getApps} = await import('firebase/app');
        let retries = 0;
        const maxRetries = 20; // 2 seconds max (20 * 100ms)
        
        while (retries < maxRetries) {
          const apps = getApps();
          if (apps.length > 0) {
            const app = apps[0];
            const apiKey = app.options?.apiKey;
            
            // Validate API key is present and valid
            if (apiKey && 
                apiKey !== '' && 
                apiKey !== 'MISSING_API_KEY' && 
                apiKey !== 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY' &&
                apiKey !== 'CONFIG_ERROR_CHECK_LOGS') {
              // Valid app found
              console.log('[AuthProvider] ✅ Firebase app validated with valid config');
              break;
            }
          }
          
          // Wait a bit before checking again
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        
        // Final validation
        const apps = getApps();
        if (apps.length === 0) {
          throw new Error('Firebase app not initialized. Check Firebase config.');
        }
        
        const app = apps[0];
        const apiKey = app.options?.apiKey;
        if (!apiKey || 
            apiKey === 'MISSING_API_KEY' || 
            apiKey === 'MISSING_API_KEY_PLEASE_SET_VITE_FIREBASE_API_KEY' ||
            apiKey === 'CONFIG_ERROR_CHECK_LOGS') {
          throw new Error(
            'Firebase app initialized with invalid API key. ' +
            'Check .env.local for VITE_FIREBASE_API_KEY and rebuild frontend.'
          );
        }
        
        if (isElectron()) {
          // Electron: Use Firebase Auth directly (no session cookies)
          await initialize();
        } else {
          // Web: Try session API first, fallback to Firebase Auth
          if (apiModule?.api) {
            try {
              await initialize(apiModule.api.session);
            } catch (error) {
              console.warn('Session API failed, using Firebase Auth:', error);
              await initialize();
            }
          } else {
            // API not loaded, use Firebase Auth directly
            await initialize();
          }
        }
        
        // Mark as initialized to prevent re-initialization
        setInitialized(true);
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Final fallback: try to initialize without session API
        try {
          await initialize();
          setInitialized(true);
        } catch (fallbackError) {
          console.error('Fallback auth initialization also failed:', fallbackError);
          // App can still work for public pages even if auth fails
          // Mark as initialized anyway to prevent retry loops
          setInitialized(true);
        }
      }
    };
    
    // Wrap in try-catch to prevent unhandled promise rejection
    initAuth().catch((error) => {
      console.error('Unhandled error in initAuth:', error);
      // Mark as initialized to prevent retry loops
      setInitialized(true);
    });

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [apiLoaded, apiModule, initialized]); // Removed initialize and cleanup from deps to prevent re-runs

  return <>{children}</>;
}

